const BACKEND_DT_RE = /^(\d{4})\.(\d{2})\.(\d{2}) (\d{2}):(\d{2})$/;

export function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

export function formatBackendDateTime(input: string): string {
  const [date, time] = input.split('T');
  if (!date || !time) {
    return input;
  }
  const [y, m, d] = date.split('-');
  if (!y || !m || !d) {
    return input;
  }
  return `${y}.${m}.${d} ${time}`;
}

export function parseBackendDateTime(value: string): Date {
  const m = BACKEND_DT_RE.exec(value.trim());
  if (!m) {
    return new Date(NaN);
  }
  const [, y, mo, d, h, mi] = m;
  return new Date(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), 0, 0);
}

export function formatBackendDateTimeFromDate(date: Date): string {
  return `${date.getFullYear()}.${pad2(date.getMonth() + 1)}.${pad2(date.getDate())} ${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

export function toLocalDateTimeInputValue(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}T${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

export function parseLocalDateTimeInputValue(value: string): Date {
  const [datePart, timePart] = value.split('T');
  if (!datePart || !timePart) {
    return new Date(NaN);
  }
  const [y, m, d] = datePart.split('-').map(Number);
  const [h, mi] = timePart.split(':').map(Number);
  return new Date(y, m - 1, d, h, mi, 0, 0);
}

export function isSameCalendarDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
}

export function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0);
}

export function endOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 0, 0);
}

export function addMonths(date: Date, delta: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + delta, 1);
}

export function startOfWeek(date: Date): Date {
  const day = startOfDay(date);
  day.setDate(day.getDate() - mondayBasedWeekday(day));
  return day;
}

export function addWeeks(date: Date, delta: number): Date {
  const next = startOfDay(date);
  next.setDate(next.getDate() + delta * 7);
  return next;
}

export function buildWeekDays(weekStart: Date): Date[] {
  const start = startOfWeek(weekStart);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

export function formatMonthYearTitle(date: Date): string {
  return new Intl.DateTimeFormat('ru-RU', { month: 'long', year: 'numeric' }).format(date);
}

export function formatDayMonthLabel(date: Date): string {
  return new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long' }).format(date);
}

export function getLocalUtcOffsetLabel(): string {
  const offsetMinutes = -new Date().getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const abs = Math.abs(offsetMinutes);
  const hours = Math.floor(abs / 60);
  const minutes = abs % 60;
  const formatted = minutes === 0 ? `${hours}` : `${hours}:${pad2(minutes)}`;
  return `UTC${sign}${formatted} (GMT${sign}${formatted})`;
}

const WEEKDAY_LABELS = ['Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота', 'Воскресенье'];
const WEEKDAY_LETTERS = ['П', 'В', 'С', 'Ч', 'П', 'С', 'В'];
const WEEKDAY_SHORT = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

export function weekdayLabel(mondayBasedIndex: number): string {
  return WEEKDAY_LABELS[mondayBasedIndex] ?? '';
}

export function weekdayLetter(mondayBasedIndex: number): string {
  return WEEKDAY_LETTERS[mondayBasedIndex] ?? '';
}

export function weekdayShort(mondayBasedIndex: number): string {
  return WEEKDAY_SHORT[mondayBasedIndex] ?? '';
}

export function mondayBasedWeekday(date: Date): number {
  return (date.getDay() + 6) % 7;
}

export function isWeekend(date: Date): boolean {
  const d = date.getDay();
  return d === 0 || d === 6;
}

export function rangeFromDays(days: Date[]): { from: Date; to: Date } {
  const from = new Date(days[0]);
  from.setHours(0, 0, 0, 0);
  const to = new Date(days[days.length - 1]);
  to.setHours(23, 59, 0, 0);
  return { from, to };
}
